import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Languages, Loader2, RotateCcw } from 'lucide-react';

/**
 * TranslateButton - Add to any user-generated content to allow translation
 * 
 * Usage:
 * <TranslateButton 
 *   text={post.content} 
 *   onTranslated={(translated) => setDisplayText(translated)}
 *   contentType="forum"
 * />
 */
const TranslateButton = ({ 
  text, 
  onTranslated, 
  contentType = 'general',
  size = 'sm', // 'sm', 'md'
  className = ''
}) => {
  const { currentLanguage, translateContent, isTranslating } = useLanguage();
  const [isTranslated, setIsTranslated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [originalText] = useState(text);

  // Don't show button if content is already in user's language or if English
  if (currentLanguage === 'en') return null;

  const handleTranslate = async () => {
    if (isTranslated) {
      // Revert to original
      onTranslated(originalText);
      setIsTranslated(false);
      return;
    }

    setLoading(true);
    try {
      const translated = await translateContent(text, contentType);
      onTranslated(translated);
      setIsTranslated(true);
    } catch (error) {
      console.error('Translation failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'text-xs px-2 py-1 gap-1',
    md: 'text-sm px-3 py-1.5 gap-1.5'
  };

  return (
    <button
      onClick={handleTranslate}
      disabled={loading || isTranslating}
      className={`
        inline-flex items-center rounded-md font-medium transition
        ${isTranslated 
          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
        }
        ${loading ? 'opacity-50 cursor-wait' : ''}
        ${sizeClasses[size]}
        ${className}
      `}
      data-testid="translate-button"
      title={isTranslated ? 'Show original' : 'Translate'}
    >
      {loading ? (
        <Loader2 className="w-3 h-3 animate-spin" />
      ) : isTranslated ? (
        <RotateCcw className="w-3 h-3" />
      ) : (
        <Languages className="w-3 h-3" />
      )}
      <span>{isTranslated ? 'Original' : 'Translate'}</span>
    </button>
  );
};

/**
 * TranslatableText - Wrapper component that handles translation state
 * 
 * Usage:
 * <TranslatableText text={post.content} contentType="forum">
 *   {(displayText, TranslateBtn) => (
 *     <div>
 *       <p>{displayText}</p>
 *       <TranslateBtn />
 *     </div>
 *   )}
 * </TranslatableText>
 */
export const TranslatableText = ({ 
  text, 
  contentType = 'general', 
  children 
}) => {
  const [displayText, setDisplayText] = useState(text);
  const { currentLanguage } = useLanguage();

  // Reset display text when original text changes
  React.useEffect(() => {
    setDisplayText(text);
  }, [text]);

  const TranslateBtn = (props) => (
    <TranslateButton 
      text={text}
      onTranslated={setDisplayText}
      contentType={contentType}
      {...props}
    />
  );

  // If English, just render children with original text
  if (currentLanguage === 'en') {
    return children(text, () => null);
  }

  return children(displayText, TranslateBtn);
};

export default TranslateButton;
